<template>
  <!--
    `closed` fires once this has actually left the DOM, transition included. Callers that
    do something positional afterwards need that: until then the scrim still covers the
    whole viewport, so anything measuring or hit-testing the page underneath sees the
    overlay rather than the page.
  -->
  <Transition
    name="feature-announcement-modal"
    @after-leave="emit('closed')"
  >
    <div
      v-if="isOpen && currentAnnouncement"
      class="feature-announcement-modal"
      role="dialog"
      aria-modal="true"
      tabindex="0"
      data-testid="feature-announcement-modal"
    >
      <div class="feature-announcement-modal__container">
        <div class="feature-announcement-modal__surface">
          <div
            class="feature-announcement-modal__card-wrapper"
            :style="{ height: cardWrapperHeight ? `${cardWrapperHeight}px` : 'auto' }"
          >
            <Transition
              :name="`feature-announcement-card-${direction}`"
              mode="out-in"
              @before-leave="onCardBeforeLeave"
              @enter="onCardEnter"
              @after-enter="onCardAfterEnter"
            >
              <FeatureAnnouncementCard
                :key="currentAnnouncement.id"
                :image="currentAnnouncement.image"
                :image-alt="currentAnnouncement.imageAlt"
                :name="currentAnnouncement.name"
                :show-cta="currentAnnouncement.showCta"
                :cta-text="currentAnnouncement.ctaText"
                @cta="handleCta"
                @dismiss="handleDismiss"
              >
                <!-- eslint-disable vue/no-v-html -->
                <span v-html="currentAnnouncement.description" />
              </FeatureAnnouncementCard>
            </Transition>
          </div>

          <div
            class="feature-announcement-modal__footer"
            data-testid="feature-announcement-modal-footer"
          >
            <AcvButton
              variant="link"
              class="feature-announcement-modal__previous"
              data-testid="feature-announcement-modal-previous"
              @click="handlePrevious"
            >
              <span v-if="!isFirst"> Previous </span>
            </AcvButton>

            <FeatureAnnouncementDots
              :total="total"
              :current-index="currentIndex"
              @select="goToIndex"
            />

            <AcvButton
              variant="primary"
              size="medium"
              class="feature-announcement-modal__next"
              data-testid="feature-announcement-modal-next"
              @click="handleNext"
            >
              {{ paginationLabel }}
            </AcvButton>
          </div>
        </div>
      </div>

      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
      <div
        class="feature-announcement-modal__scrim"
        data-testid="feature-announcement-modal-scrim"
        @click="handleDismiss"
      />
    </div>
  </Transition>
</template>

<script setup>
import { AcvButton } from '@acv/shared-vue';
import { onKeyStroke } from '@vueuse/core';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  announcements: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['card-displayed', 'closed', 'cta', 'dismiss']);

const currentIndex = ref(0);
const direction = ref('forward');

/**
 * Locks the card wrapper to an explicit pixel height while a card is leaving/entering,
 * so a height change between cards of different content length animates via CSS
 * (`transition: height`) instead of jumping. Reset to null (auto) once settled so
 * later natural reflows (e.g. window resize) aren't pinned to a stale pixel value.
 */
const cardWrapperHeight = ref(null);

const onCardBeforeLeave = (el) => {
  cardWrapperHeight.value = el.offsetHeight;
};

const onCardEnter = (el) => {
  cardWrapperHeight.value = el.offsetHeight;
};

const onCardAfterEnter = () => {
  cardWrapperHeight.value = null;
};

/**
 * Snapshot of the batch taken when the modal opens. Paginating against a live prop
 * would let the array shift under the user mid-batch — e.g. if the wiring layer marks
 * a card "seen" the moment it's displayed and that feeds a store getter this prop is
 * bound to, the card could drop out of the array immediately, shrinking `total` and
 * the dots count while the user is still looking at it. Once open, this component
 * ignores further live changes to `announcements` until it closes and reopens.
 */
const batch = ref([]);

const total = computed(() => batch.value.length);
const currentAnnouncement = computed(() => batch.value[currentIndex.value] ?? null);
const isFirst = computed(() => currentIndex.value === 0);
const isLast = computed(() => currentIndex.value === total.value - 1);
const paginationLabel = computed(() => (isLast.value ? 'Got it' : 'Next'));

const emitDisplayed = () => {
  if (currentAnnouncement.value) {
    emit('card-displayed', currentAnnouncement.value.id);
  }
};

const setCurrentIndex = (index) => {
  direction.value = index >= currentIndex.value ? 'forward' : 'backward';
  currentIndex.value = index;
  emitDisplayed();
};

/**
 * Overlay stacking/Escape-key ownership, mirroring UI/Modal.vue's useModalControls
 * idiom directly (rather than reusing that component), since Card already supplies
 * this modal's only header/close chrome. Declared before the isOpen watcher below
 * (which calls registerModal/unregisterModal) since that watcher runs immediately.
 */
const { registerModal, unregisterModal, isTopmostModal } = useModalStack();

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      batch.value = [...props.announcements];
      direction.value = 'forward';
      currentIndex.value = 0;
      registerModal();
      emitDisplayed();
    } else {
      unregisterModal();
    }
  },
  { immediate: true },
);

const handlePrevious = () => {
  if (!isFirst.value) {
    setCurrentIndex(currentIndex.value - 1);
  }
};

const handleNext = () => {
  if (isLast.value) {
    emit('dismiss');
  } else {
    setCurrentIndex(currentIndex.value + 1);
  }
};

const handleDismiss = () => {
  emit('dismiss');
};

const handleCta = () => {
  if (currentAnnouncement.value) {
    emit('cta', currentAnnouncement.value.id);
  }
};

const goToIndex = (index) => {
  if (index !== currentIndex.value) {
    setCurrentIndex(index);
  }
};

onKeyStroke('Escape', () => {
  if (props.isOpen && isTopmostModal()) {
    handleDismiss();
  }
});

onBeforeUnmount(() => {
  unregisterModal();
});
</script>

<style lang="scss" scoped>
.feature-announcement-card-forward-enter-active,
.feature-announcement-card-forward-leave-active,
.feature-announcement-card-backward-enter-active,
.feature-announcement-card-backward-leave-active {
  transition:
    opacity 0.2s ease-in-out,
    transform 0.4s ease-in-out;
}

.feature-announcement-card-forward-enter-from,
.feature-announcement-card-backward-leave-to {
  @apply tw-opacity-0;
  transform: translateX(12px);
}

.feature-announcement-card-forward-leave-to,
.feature-announcement-card-backward-enter-from {
  @apply tw-opacity-0;
  transform: translateX(-12px);
}

.feature-announcement-modal-enter-active,
.feature-announcement-modal-leave-active {
  transition: opacity 0.3s ease-out;

  .feature-announcement-modal__surface {
    transition: transform 0.3s ease-out;
  }
}

.feature-announcement-modal-enter-from,
.feature-announcement-modal-leave-to {
  @apply tw-opacity-0;

  .feature-announcement-modal__surface {
    @apply tw-translate-y-4;
  }
}

.feature-announcement-modal {
  @apply tw-fixed tw-left-0 tw-top-0 tw-z-50 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center;

  &__container {
    @apply tw-pointer-events-none tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center;
  }

  &__surface {
    @apply tw-pointer-events-auto tw-flex tw-w-full tw-max-w-md tw-flex-col tw-rounded-2xl tw-bg-white tw-shadow-lg;
  }

  &__card-wrapper {
    @apply tw-overflow-hidden;
    transition: height 0.1s ease-in-out;
  }

  &__footer {
    @apply tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-pb-4 tw-md:tw-px-6 tw-md:tw-pb-6;
  }

  &__scrim {
    @apply tw-fixed tw-left-0 tw-top-0 tw-h-svh tw-w-svw tw-bg-black tw-opacity-25;
    z-index: -1;
  }

  &__previous,
  &__next {
    @apply tw-min-w-25;
  }
}
</style>
